﻿import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useT } from "@/lib/i18n";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import type {
  CreatePromotionRequest,
  CurrencyCode,
  EventSummary,
  Promotion,
  PromotionTypeValue,
  TicketType,
} from "@ticketnjoy/types";
import { SUPPORTED_CURRENCIES } from "@ticketnjoy/types";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useEventQuery, useEventsQuery } from "@/features/events/api";

/**
 * Promosyon create/edit formu. Tek bir kapsam seçilebilir:
 * - organizer-wide (event = null, ticketTypes = [])
 * - event-level (event seçili, ticketTypes = [])
 * - ticketType-level (event seçili, ticketTypes = [ids])
 *
 * `datetime-local` input'lar local saati alır, submit'te ISO string'e çevrilir.
 */
const schema = z
  .object({
    name: z.string().min(2).max(200),
    description: z.string().max(2000).optional().or(z.literal("")),
    code: z
      .string()
      .regex(/^[A-Za-z0-9_-]+$/, "Yalnızca harf, rakam, tire, alt tire.")
      .min(2)
      .max(50)
      .optional()
      .or(z.literal("")),
    type: z.enum(["BuyXGetY", "Percentage", "FixedAmount"]),
    buyX: z.coerce.number().int().min(1).optional().nullable(),
    getY: z.coerce.number().int().min(1).optional().nullable(),
    discountValue: z.coerce.number().min(0).optional().nullable(),
    discountCurrency: z.enum(SUPPORTED_CURRENCIES).optional().nullable(),
    startsAt: z.string().optional().or(z.literal("")),
    endsAt: z.string().optional().or(z.literal("")),
    eventId: z.string().optional().or(z.literal("")),
    ticketTypeIds: z.array(z.string()).default([]),
    maxUses: z.coerce.number().int().min(1).optional().nullable(),
    priority: z.coerce.number().int().min(0).max(1000).default(0),
    isActive: z.boolean().default(true),
  })
  .superRefine((val, ctx) => {
    if (val.type === "BuyXGetY") {
      if (!val.buyX || val.buyX < 1)
        ctx.addIssue({
          code: "custom",
          path: ["buyX"],
          message: "BuyX en az 1 olmalı.",
        });
      if (!val.getY || val.getY < 1)
        ctx.addIssue({
          code: "custom",
          path: ["getY"],
          message: "GetY en az 1 olmalı.",
        });
    }
    if (val.type === "Percentage") {
      if (val.discountValue == null || val.discountValue <= 0 || val.discountValue > 100)
        ctx.addIssue({
          code: "custom",
          path: ["discountValue"],
          message: "% 0 ile 100 arasında olmalı.",
        });
    }
    if (val.type === "FixedAmount") {
      if (val.discountValue == null || val.discountValue <= 0)
        ctx.addIssue({
          code: "custom",
          path: ["discountValue"],
          message: "Tutar 0'dan büyük olmalı.",
        });
      if (!val.discountCurrency)
        ctx.addIssue({
          code: "custom",
          path: ["discountCurrency"],
          message: "Para birimi seç.",
        });
    }
    if (val.startsAt && val.endsAt) {
      const s = new Date(val.startsAt).getTime();
      const e = new Date(val.endsAt).getTime();
      if (!Number.isNaN(s) && !Number.isNaN(e) && s >= e) {
        ctx.addIssue({
          code: "custom",
          path: ["endsAt"],
          message: "Bitiş, başlangıçtan sonra olmalı.",
        });
      }
    }
  });

export type PromotionFormValues = z.infer<typeof schema>;
type PromotionFormInput = z.input<typeof schema>;

/**
 * zod v4'te `z.coerce.number()` input tipi `unknown` — RHF <Input> value prop'u
 * bunu kabul etmiyor. Bu helper null/undefined → "" dönüştürüp string/number'a daraltır.
 */
function inputValue(v: unknown, fallback: string | number = ""): string | number {
  if (v == null) return fallback;
  if (typeof v === "number" || typeof v === "string") return v;
  return fallback;
}

interface PromotionFormProps {
  mode: "create" | "edit";
  initial?: Promotion;
  onSubmit: (payload: CreatePromotionRequest) => Promise<void> | void;
  isSubmitting?: boolean;
  submitLabel?: string;
}

export function PromotionForm({
  mode,
  initial,
  onSubmit,
  isSubmitting,
  submitLabel,
}: PromotionFormProps) {
  const t = useT();
  const defaults = useMemo<PromotionFormInput>(() => toFormDefaults(initial), [initial]);
  const form = useForm<PromotionFormInput, unknown, PromotionFormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
  });

  const type = form.watch("type");
  const eventId = form.watch("eventId");

  const { data: eventsList } = useEventsQuery();

  const eventSlug = useMemo(() => {
    if (!eventId) return undefined;
    return eventsList?.find((e) => e.id === eventId)?.slug;
  }, [eventId, eventsList]);

  const { data: eventDetail } = useEventQuery(eventSlug ?? "");

  useEffect(() => {
    const current = form.getValues("ticketTypeIds") ?? [];
    if (!current.length) return;
    if (!eventDetail) return;
    const valid = new Set(eventDetail.ticketTypes.map((tt) => tt.id));
    const filtered = current.filter((id) => valid.has(id));
    if (filtered.length !== current.length) {
      form.setValue("ticketTypeIds", filtered, { shouldValidate: false });
    }
  }, [eventDetail, form]);

  useEffect(() => {
    if (!eventId) {
      const cur = form.getValues("ticketTypeIds") ?? [];
      if (cur.length > 0) {
        form.setValue("ticketTypeIds", [], { shouldValidate: false });
      }
    }
  }, [eventId, form]);

  const handleSubmit = form.handleSubmit(async (values) => {
    const payload: CreatePromotionRequest = {
      name: values.name.trim(),
      description: values.description?.trim() || null,
      code: values.code?.trim() || null,
      type: values.type,
      buyX: values.type === "BuyXGetY" ? (values.buyX ?? null) : null,
      getY: values.type === "BuyXGetY" ? (values.getY ?? null) : null,
      discountValue:
        values.type === "BuyXGetY" ? null : (values.discountValue ?? null),
      discountCurrency:
        values.type === "FixedAmount"
          ? ((values.discountCurrency ?? null) as CurrencyCode | null)
          : null,
      startsAt: values.startsAt ? new Date(values.startsAt).toISOString() : null,
      endsAt: values.endsAt ? new Date(values.endsAt).toISOString() : null,
      eventId: values.eventId || null,
      ticketTypeIds: values.eventId ? (values.ticketTypeIds ?? []) : [],
      maxUses: values.maxUses ?? null,
      priority: values.priority ?? 0,
      isActive: values.isActive,
    };
    await onSubmit(payload);
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>{t("promotions.fields.name")} *</FormLabel>
                <FormControl>
                  <Input placeholder={t("promotions.placeholders.name")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>{t("promotions.fields.description")}</FormLabel>
                <FormControl>
                  <Textarea
                    rows={3}
                    placeholder={t("promotions.placeholders.description")}
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("promotions.fields.code")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("promotions.placeholders.code")}
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormDescription>{t("promotions.codeDesc")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="priority"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("promotions.fields.priority")}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    max={1000}
                    step={1}
                    {...field}
                    value={inputValue(field.value, 0)}
                  />
                </FormControl>
                <FormDescription>{t("promotions.priorityDesc")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Tip ve parametreler */}
        <div className="space-y-4 rounded-lg border p-4">
          <h3 className="text-sm font-semibold">{t("promotions.typeSection")}</h3>

          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("promotions.fields.type")} *</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={(v) => field.onChange(v as PromotionTypeValue)}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="BuyXGetY">
                      {t("promotions.typeLabels.BuyXGetY")}
                    </SelectItem>
                    <SelectItem value="Percentage">
                      {t("promotions.typeLabels.Percentage")}
                    </SelectItem>
                    <SelectItem value="FixedAmount">
                      {t("promotions.typeLabels.FixedAmount")}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {type === "BuyXGetY" && (
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="buyX"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("promotions.fields.buyX")} *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        step={1}
                        {...field}
                        value={inputValue(field.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="getY"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("promotions.fields.getY")} *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        step={1}
                        {...field}
                        value={inputValue(field.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <p className="text-xs text-muted-foreground sm:col-span-2">
                {t("promotions.bogoHint")}
              </p>
            </div>
          )}

          {type === "Percentage" && (
            <FormField
              control={form.control}
              name="discountValue"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("promotions.fields.percentage")} *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      {...field}
                      value={inputValue(field.value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {type === "FixedAmount" && (
            <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
              <FormField
                control={form.control}
                name="discountValue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("promotions.fields.amount")} *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        {...field}
                        value={inputValue(field.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="discountCurrency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("promotions.fields.currency")} *</FormLabel>
                    <Select
                      value={field.value ?? ""}
                      onValueChange={(v) => field.onChange(v as CurrencyCode)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="TRY" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {SUPPORTED_CURRENCIES.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
        </div>

        {/* Zamanlama */}
        <div className="space-y-4 rounded-lg border p-4">
          <h3 className="text-sm font-semibold">{t("promotions.scheduleSection")}</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="startsAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("promotions.fields.startsAt")}</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="endsAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("promotions.fields.endsAt")}</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="maxUses"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("promotions.fields.maxUses")}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    placeholder={t("promotions.placeholders.maxUses")}
                    {...field}
                    value={inputValue(field.value)}
                  />
                </FormControl>
                <FormDescription>{t("promotions.maxUsesDesc")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Kapsam */}
        <div className="space-y-4 rounded-lg border p-4">
          <h3 className="text-sm font-semibold">{t("promotions.scopeSection")}</h3>

          <FormField
            control={form.control}
            name="eventId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("promotions.fields.event")}</FormLabel>
                <Select
                  value={field.value || "__all__"}
                  onValueChange={(v) =>
                    field.onChange(v === "__all__" ? "" : v)
                  }
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="__all__">
                      {t("promotions.scopeAll")}
                    </SelectItem>
                    {(eventsList ?? []).map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>{t("promotions.eventDesc")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {eventId && eventDetail ? (
            <TicketTypePicker
              form={form}
              ticketTypes={eventDetail.ticketTypes}
              eventsList={eventsList}
            />
          ) : null}
        </div>

        {/* Aktif */}
        <FormField
          control={form.control}
          name="isActive"
          render={({ field }) => (
            <FormItem className="flex items-center gap-3 rounded-lg border p-4">
              <FormControl>
                <input
                  type="checkbox"
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
              </FormControl>
              <div className="flex-1">
                <FormLabel className="m-0">
                  {t("promotions.fields.isActive")}
                </FormLabel>
                <FormDescription className="mt-1">
                  {t("promotions.isActiveDesc")}
                </FormDescription>
              </div>
            </FormItem>
          )}
        />

        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {submitLabel ??
              (mode === "create" ? t("promotions.submitCreate") : t("promotions.submitUpdate"))}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function TicketTypePicker({
  form,
  ticketTypes,
  eventsList,
}: {
  form: ReturnType<typeof useForm<PromotionFormInput, unknown, PromotionFormValues>>;
  ticketTypes: TicketType[];
  eventsList: EventSummary[] | undefined;
}) {
  const t = useT();
  const selected = form.watch("ticketTypeIds") ?? [];

  function toggle(id: string) {
    const set = new Set(selected);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    form.setValue("ticketTypeIds", Array.from(set), { shouldValidate: true });
  }

  if (!ticketTypes.length) {
    return (
      <p className="text-xs text-muted-foreground">
        {t("promotions.noTicketTypes")}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <FormLabel>{t("promotions.fields.ticketTypes")}</FormLabel>
      <p className="text-xs text-muted-foreground">
        {t("promotions.ticketTypesDesc")}
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {ticketTypes.map((tt) => {
          const checked = selected.includes(tt.id);
          return (
            <label
              key={tt.id}
              className="flex cursor-pointer items-center gap-2 rounded-md border p-2 text-sm hover:bg-accent/40"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(tt.id)}
                className="h-4 w-4 rounded border-input"
              />
              <span className="flex-1 truncate">{tt.name}</span>
              <span className="text-xs text-muted-foreground">{tt.currency}</span>
            </label>
          );
        })}
      </div>
      {eventsList == null && (
        <p className="text-xs text-muted-foreground">
          <Loader2 className="inline h-3 w-3 animate-spin" />
        </p>
      )}
    </div>
  );
}

function toFormDefaults(p?: Promotion): PromotionFormInput {
  if (!p) {
    return {
      name: "",
      description: "",
      code: "",
      type: "BuyXGetY",
      buyX: 2,
      getY: 1,
      discountValue: null,
      discountCurrency: null,
      startsAt: "",
      endsAt: "",
      eventId: "",
      ticketTypeIds: [],
      maxUses: null,
      priority: 0,
      isActive: true,
    };
  }
  return {
    name: p.name,
    description: p.description ?? "",
    code: p.code ?? "",
    type: p.type,
    buyX: p.buyX ?? null,
    getY: p.getY ?? null,
    discountValue: p.discountValue ?? null,
    discountCurrency: p.discountCurrency ?? null,
    startsAt: toLocalInput(p.startsAt),
    endsAt: toLocalInput(p.endsAt),
    eventId: p.eventId ?? "",
    ticketTypeIds: p.ticketTypes.map((tt) => tt.id),
    maxUses: p.maxUses ?? null,
    priority: p.priority,
    isActive: p.isActive,
  };
}

function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
